import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCommentDots, FaTimes, FaPaperPlane, FaRobot } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import './ChatBot.css';

const CHAT_API_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3940/api/chat'
    : '/api/chat';

const LEAD_API_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3940/api/lead'
    : '/api/lead';

const WELCOME = {
  role: 'assistant',
  content:
    "Hi! I'm the TSP Assistant. Ask me about our products, manufacturing processes, quality assurance, or locations — and I can connect you with a sales executive any time.",
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [leadPrompt, setLeadPrompt] = useState(null); // { summary }
  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, leadPrompt, sending]);

  const sendMessage = useCallback(
    async (text) => {
      const userMsg = { role: 'user', content: text };
      const nextMessages = [...messages, userMsg];
      setMessages([...nextMessages, { role: 'assistant', content: '' }]);
      setSending(true);

      try {
        const res = await fetch(CHAT_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: nextMessages.map(({ role, content }) => ({ role, content })),
          }),
        });

        if (!res.ok || !res.body) {
          throw new Error(`Chat request failed (${res.status})`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let assistantText = '';
        let toolTriggered = null;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = rawEvent.split('\n').find((l) => l.startsWith('data: '));
            if (!line) continue;
            let payload;
            try {
              payload = JSON.parse(line.slice(6));
            } catch {
              continue;
            }

            if (payload.type === 'text') {
              assistantText += payload.text;
              const snapshot = assistantText;
              setMessages((prev) => {
                const copy = prev.slice();
                copy[copy.length - 1] = { role: 'assistant', content: snapshot };
                return copy;
              });
            } else if (payload.type === 'tool_use' && payload.name === 'collect_lead_info') {
              toolTriggered = payload.input || {};
            } else if (payload.type === 'error') {
              throw new Error(payload.message || 'Chat error');
            }
          }
        }

        if (!assistantText) {
          setMessages((prev) => {
            const copy = prev.slice();
            copy[copy.length - 1] = {
              role: 'assistant',
              content: "I'd like to connect you with our sales team for that. Please share your details below.",
            };
            return copy;
          });
        }

        if (toolTriggered) {
          setLeadPrompt({ summary: toolTriggered.summary || '' });
        }
      } catch (err) {
        console.error('[chat] error', err);
        setMessages((prev) => {
          const copy = prev.slice();
          copy[copy.length - 1] = {
            role: 'assistant',
            content:
              "I'm having trouble reaching our assistant right now. You can email prod_sales@tokyosteel.com.ph or call +63-32-340-3753 in the meantime.",
          };
          return copy;
        });
      } finally {
        setSending(false);
      }
    },
    [messages]
  );

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    sendMessage(text);
  };

  const handleLeadSuccess = () => {
    setLeadPrompt(null);
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content:
          "Thanks! I've forwarded your details to our sales executive. They'll reach out within one business day.",
      },
    ]);
  };

  const handleLeadCancel = () => {
    setLeadPrompt(null);
  };

  return (
    <div className="chatbot">
      {isOpen && (
        <div className="chatbot__window">
          <div className="chatbot__header">
            <div className="chatbot__header-info">
              <FaRobot className="chatbot__header-icon" />
              <div>
                <h4>TSP Assistant</h4>
                <span>Online</span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="chatbot__close" aria-label="Close chat">
              <FaTimes />
            </button>
          </div>

          <div className="chatbot__messages" ref={messagesRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot__msg chatbot__msg--${msg.role === 'user' ? 'user' : 'bot'}`}>
                {msg.content ? (
                  msg.role === 'user' ? (
                    msg.content
                  ) : (
                    <ReactMarkdown
                      components={{
                        a: ({ node, children, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  )
                ) : sending && i === messages.length - 1 ? (
                  <TypingDots />
                ) : null}
              </div>
            ))}

            {leadPrompt && (
              <LeadForm
                context={leadPrompt.summary}
                transcript={messages}
                onSuccess={handleLeadSuccess}
                onCancel={handleLeadCancel}
              />
            )}
          </div>

          <form onSubmit={handleSend} className="chatbot__input-area">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              className="chatbot__input"
              disabled={sending}
            />
            <button type="submit" className="chatbot__send" disabled={sending || !input.trim()} aria-label="Send">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}

      <button
        className={`chatbot__fab ${isOpen ? 'chatbot__fab--hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <FaCommentDots />
      </button>
    </div>
  );
};

const TypingDots = () => (
  <span className="chatbot__typing" aria-label="Assistant is typing">
    <span />
    <span />
    <span />
  </span>
);

const LeadForm = ({ context, transcript, onSuccess, onCancel }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('Please fill in name, email, and phone.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(LEAD_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: note.trim(),
          context,
          honeypot,
          transcript: (transcript || [])
            .filter((m) => m && m.content && m.content.trim())
            .map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to send.');
      }
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to send.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="chatbot__lead-form" onSubmit={submit}>
      <div className="chatbot__lead-title">Connect with a sales executive</div>
      <input
        className="chatbot__lead-input"
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        autoComplete="name"
      />
      <input
        className="chatbot__lead-input"
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
      />
      <input
        className="chatbot__lead-input"
        type="tel"
        placeholder="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
        autoComplete="tel"
      />
      <textarea
        className="chatbot__lead-input chatbot__lead-textarea"
        placeholder="Anything else we should know? (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
      />
      <input
        type="text"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="chatbot__lead-hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      {error && <div className="chatbot__lead-error">{error}</div>}
      <div className="chatbot__lead-actions">
        <button type="button" className="chatbot__lead-cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="chatbot__lead-submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  );
};

export default ChatBot;
